import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './SignUp.css';

const SignUp = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    roles: [],
    // Champs employé (requis si RH ou MANAGER)
    nom: '',
    prenom: '',
    dateNaissance: '',
    telephone: '',
    cin: '',
    cnssNumber: '',
    maritalStatus: 'SINGLE',
    children: 0,
    qualification: '',
    categorie: '',
    echellon: '',
    dateEmbauche: '',
    typeContrat: 'CDI',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState('');

  const { signup } = useAuth();
  const navigate = useNavigate();

  const isRHOrManager = formData.roles.includes('rh') || formData.roles.includes('manager');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => {
        const roles = prev.roles || [];
        if (checked) {
          return { ...prev, roles: [...roles, value] };
        } else {
          return { ...prev, roles: roles.filter(r => r !== value) };
        }
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (signupError) {
      setSignupError('');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est obligatoire';
    } else if (formData.username.length < 3 || formData.username.length > 50) {
      newErrors.username = 'Le nom d\'utilisateur doit contenir entre 3 et 50 caractères';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'L\'email doit être valide';
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est obligatoire';
    } else if (formData.password.length < 6 || formData.password.length > 100) {
      newErrors.password = 'Le mot de passe doit contenir entre 6 et 100 caractères';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est obligatoire';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    // Validation des champs employé si RH ou MANAGER
    if (isRHOrManager) {
      if (!formData.nom.trim()) {
        newErrors.nom = 'Le nom est obligatoire';
      }
      if (!formData.prenom.trim()) {
        newErrors.prenom = 'Le prénom est obligatoire';
      }
      if (!formData.dateNaissance) {
        newErrors.dateNaissance = 'La date de naissance est obligatoire';
      }
      if (!formData.telephone.trim()) {
        newErrors.telephone = 'Le téléphone est obligatoire';
      }
      if (!formData.cin.trim()) {
        newErrors.cin = 'Le CIN est obligatoire';
      } else if (!/^[0-9]{8}$/.test(formData.cin)) {
        newErrors.cin = 'Le CIN doit contenir exactement 8 chiffres';
      }
      if (!formData.cnssNumber.trim()) {
        newErrors.cnssNumber = 'Le numéro CNSS est obligatoire';
      } else if (!/^[0-9]{8,13}$/.test(formData.cnssNumber)) {
        newErrors.cnssNumber = 'Le numéro CNSS doit contenir entre 8 et 13 chiffres';
      }
      if (!formData.qualification.trim()) {
        newErrors.qualification = 'La qualification est obligatoire';
      }
      if (!formData.dateEmbauche) {
        newErrors.dateEmbauche = 'La date d\'embauche est obligatoire';
      }
      if (!formData.typeContrat) {
        newErrors.typeContrat = 'Le type de contrat est obligatoire';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSignupError('');

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        roles: formData.roles.length > 0 ? formData.roles : ['user'],
      };

      // Ajouter les champs employé si RH ou MANAGER
      if (isRHOrManager) {
        userData.nom = formData.nom;
        userData.prenom = formData.prenom;
        userData.dateNaissance = formData.dateNaissance;
        userData.telephone = formData.telephone;
        userData.cin = formData.cin;
        userData.cnssNumber = formData.cnssNumber;
        userData.maritalStatus = formData.maritalStatus;
        userData.children = parseInt(formData.children) || 0;
        userData.qualification = formData.qualification;
        userData.categorie = formData.categorie;
        userData.echellon = formData.echellon;
        userData.dateEmbauche = formData.dateEmbauche;
        userData.typeContrat = formData.typeContrat;
      }

      const result = await signup(userData);
      if (result.success) {
        navigate('/login', { 
          state: { message: 'Inscription réussie ! Vous pouvez maintenant vous connecter.' }
        });
      } else {
        setSignupError(result.error || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      setSignupError('Une erreur est survenue lors de l\'inscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <h2>Inscription</h2>
          <p>Créer un nouveau compte</p>
        </div>

        {signupError && (
          <div className="error-banner">
            {signupError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={errors.username ? 'error' : ''}
              placeholder="Choisissez un nom d'utilisateur"
              autoComplete="username"
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="votre.email@example.com"
              autoComplete="email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe *</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder="Minimum 6 caractères"
              autoComplete="new-password"
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe *</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder="Répétez le mot de passe"
              autoComplete="new-password"
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label>Rôles *</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  value="user"
                  checked={formData.roles.includes('user')}
                  onChange={handleChange}
                />
                <span>Utilisateur</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  value="manager"
                  checked={formData.roles.includes('manager')}
                  onChange={handleChange}
                />
                <span>Manager</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  value="rh"
                  checked={formData.roles.includes('rh')}
                  onChange={handleChange}
                />
                <span>RH</span>
              </label>
            </div>
            {isRHOrManager && (
              <p className="form-hint warning">
                ⚠️ Les rôles RH et Manager nécessitent de compléter les informations employé ci-dessous
              </p>
            )}
          </div>

          {/* Champs employé (affichés uniquement si RH ou MANAGER) */}
          {isRHOrManager && (
            <div className="employee-section">
              <h3>Informations employé (obligatoires pour RH et Manager)</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom">Nom *</label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={errors.nom ? 'error' : ''}
                  />
                  {errors.nom && <span className="error-message">{errors.nom}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="prenom">Prénom *</label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    className={errors.prenom ? 'error' : ''}
                  />
                  {errors.prenom && <span className="error-message">{errors.prenom}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateNaissance">Date de naissance *</label>
                  <input
                    type="date"
                    id="dateNaissance"
                    name="dateNaissance"
                    value={formData.dateNaissance}
                    onChange={handleChange}
                    className={errors.dateNaissance ? 'error' : ''}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {errors.dateNaissance && <span className="error-message">{errors.dateNaissance}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="telephone">Téléphone *</label>
                  <input
                    type="text"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className={errors.telephone ? 'error' : ''}
                    placeholder="Ex: +216 21 776 772"
                  />
                  {errors.telephone && <span className="error-message">{errors.telephone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cin">CIN *</label>
                  <input
                    type="text"
                    id="cin"
                    name="cin"
                    value={formData.cin}
                    onChange={handleChange}
                    className={errors.cin ? 'error' : ''}
                    maxLength="8"
                    placeholder="8 chiffres"
                  />
                  {errors.cin && <span className="error-message">{errors.cin}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="cnssNumber">Numéro CNSS *</label>
                  <input
                    type="text"
                    id="cnssNumber"
                    name="cnssNumber"
                    value={formData.cnssNumber}
                    onChange={handleChange}
                    className={errors.cnssNumber ? 'error' : ''}
                    maxLength="13"
                    placeholder="8-13 chiffres"
                  />
                  {errors.cnssNumber && <span className="error-message">{errors.cnssNumber}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="maritalStatus">Situation familiale *</label>
                  <select
                    id="maritalStatus"
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                  >
                    <option value="SINGLE">Célibataire</option>
                    <option value="MARRIED">Marié(e)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="children">Nombre d'enfants *</label>
                  <input
                    type="number"
                    id="children"
                    name="children"
                    value={formData.children}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="qualification">Qualification *</label>
                  <input
                    type="text"
                    id="qualification"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    className={errors.qualification ? 'error' : ''}
                    placeholder="Ex: Responsable RH"
                  />
                  {errors.qualification && <span className="error-message">{errors.qualification}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="typeContrat">Type de contrat *</label>
                  <select
                    id="typeContrat"
                    name="typeContrat"
                    value={formData.typeContrat}
                    onChange={handleChange}
                    className={errors.typeContrat ? 'error' : ''}
                  >
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                    <option value="STAGE">STAGE</option>
                    <option value="FREELANCE">FREELANCE</option>
                    <option value="SIVP">SIVP</option>
                    <option value="KARAMA">KARAMA</option>
                    <option value="GERANT">GÉRANT</option>
                  </select>
                  {errors.typeContrat && <span className="error-message">{errors.typeContrat}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="categorie">Catégorie</label>
                  <select
                    id="categorie"
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleChange}
                  >
                    <option value="">Sélectionner</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="echellon">Échelon</label>
                  <select
                    id="echellon"
                    name="echellon"
                    value={formData.echellon}
                    onChange={handleChange}
                  >
                    <option value="">Sélectionner</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="dateEmbauche">Date d'embauche *</label>
                <input
                  type="date"
                  id="dateEmbauche"
                  name="dateEmbauche"
                  value={formData.dateEmbauche}
                  onChange={handleChange}
                  className={errors.dateEmbauche ? 'error' : ''}
                  max={new Date().toISOString().split('T')[0]}
                />
                {errors.dateEmbauche && <span className="error-message">{errors.dateEmbauche}</span>}
              </div>
            </div>
          )}

          <button 
            type="submit" 
            className="btn btn-primary btn-block"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Inscription...' : 'S\'inscrire'}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Déjà un compte ?{' '}
            <Link to="/login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;

